// ============================================================================
// WMComposerInsights — Sprint S06 + S07 + S08
// ----------------------------------------------------------------------------
// Painel admin com a telemetria do composer shadow + comparação com legacy +
// controles de promoção a canary (S08):
//   • Volume de decisões (24h) — separado por source (shadow / legacy)
//   • Concordância shadow vs legacy (S07)
//   • Drift histórico + botão de promoção (S08, em <CanaryControl />)
//   • Distribuição por `decision` (respond / silent / defer)
//   • Top reason_codes (apenas shadow)
//   • Latência p50/p95/p99 do composer
//   • Frequência dos sinais reais (S05)
// ============================================================================

import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { Activity, Clock, GitBranch, GitCompare, Info, Radio, Target } from "lucide-react";
// CanaryControl removido em C4.3 — Cortex é binário, sem rollout gradual.
// DivergenceExplorer removido em Plano-13 (sweep canary residual).
import { ShadowModeBanner } from "@/components/admin/ShadowModeBanner";

type DecisionRow = {
  id: string;
  decision: "respond" | "silent" | "defer";
  reason_code: string;
  latency_ms: number | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  message_id: string;
  decision_source: "shadow" | "legacy";
};

const WINDOW_HOURS = 24;
const REFRESH_MS = 30_000;

function percentile(sorted: number[], p: number): number {
  if (!sorted.length) return 0;
  const idx = Math.min(sorted.length - 1, Math.floor(sorted.length * p));
  return sorted[idx] ?? 0;
}

function sigBoolean(meta: Record<string, unknown> | null, key: string): boolean {
  if (!meta || typeof meta !== "object") return false;
  const sig = (meta as { signals?: Record<string, unknown> }).signals;
  return Boolean(sig?.[key]);
}

function sigNumber(meta: Record<string, unknown> | null, key: string): number {
  if (!meta || typeof meta !== "object") return 0;
  const sig = (meta as { signals?: Record<string, unknown> }).signals;
  return Number(sig?.[key] ?? 0);
}

/**
 * S07: pareia shadow ↔ legacy por message_id e calcula concordância.
 * Considera concordante quando ambos resultam em "respond" OU ambos em
 * algo-que-não-respond ({silent, defer}).
 */
function computeAgreement(rows: DecisionRow[]) {
  const byMsg = new Map<string, { shadow?: DecisionRow; legacy?: DecisionRow }>();
  for (const r of rows) {
    if (!r.message_id) continue;
    const slot = byMsg.get(r.message_id) ?? {};
    slot[r.decision_source] = r;
    byMsg.set(r.message_id, slot);
  }
  let paired = 0;
  let agree = 0;
  let bothRespond = 0;
  let bothSilent = 0;
  let shadowRespondLegacySilent = 0;
  let shadowSilentLegacyRespond = 0;
  for (const slot of byMsg.values()) {
    if (!slot.shadow || !slot.legacy) continue;
    paired++;
    const sResp = slot.shadow.decision === "respond";
    const lResp = slot.legacy.decision === "respond";
    if (sResp && lResp) { bothRespond++; agree++; }
    else if (!sResp && !lResp) { bothSilent++; agree++; }
    else if (sResp && !lResp) shadowRespondLegacySilent++;
    else shadowSilentLegacyRespond++;
  }
  const pct = paired > 0 ? (100 * agree) / paired : 0;
  return { paired, agree, pct, bothRespond, bothSilent, shadowRespondLegacySilent, shadowSilentLegacyRespond };
}

export default function WMComposerInsights() {
  const [rows, setRows] = useState<DecisionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const since = new Date(Date.now() - WINDOW_HOURS * 3_600_000).toISOString();
      const { data, error } = await supabase
        .from("syra_invocation_decisions")
        .select("id, decision, reason_code, latency_ms, metadata, created_at, message_id, decision_source")
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .limit(10000);
      if (cancelled) return;
      if (error) {
        setErrMsg(error.message);
      } else {
        setErrMsg(null);
        setRows((data ?? []) as DecisionRow[]);
      }
      setLoading(false);
    };
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  // Separa shadow / legacy
  const shadowRows = rows.filter((r) => r.decision_source === "shadow");
  const legacyRows = rows.filter((r) => r.decision_source === "legacy");
  const totalShadow = shadowRows.length;
  const totalLegacy = legacyRows.length;

  // KPIs sobre o shadow (composer é o foco do painel)
  const byDecision = shadowRows.reduce<Record<string, number>>((acc, r) => {
    acc[r.decision] = (acc[r.decision] ?? 0) + 1;
    return acc;
  }, {});
  const byReason = Object.entries(
    shadowRows.reduce<Record<string, number>>((acc, r) => {
      acc[r.reason_code] = (acc[r.reason_code] ?? 0) + 1;
      return acc;
    }, {}),
  )
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8);

  const sortedLat = shadowRows
    .map((r) => r.latency_ms ?? 0)
    .filter((n) => n > 0)
    .sort((a, b) => a - b);
  const p50 = percentile(sortedLat, 0.5);
  const p95 = percentile(sortedLat, 0.95);
  const p99 = percentile(sortedLat, 0.99);

  const sigStats = {
    is_mention: shadowRows.filter((r) => sigBoolean(r.metadata, "is_mention")).length,
    is_reply_to_agent: shadowRows.filter((r) => sigBoolean(r.metadata, "is_reply_to_agent")).length,
    is_direct_dm: shadowRows.filter((r) => sigBoolean(r.metadata, "is_direct_dm")).length,
    rule_3msgs_active: shadowRows.filter((r) => sigNumber(r.metadata, "recent_agent_msg_count") >= 3).length,
  };

  // S07: concordância
  const agreement = computeAgreement(rows);

  return (
    <div className="container max-w-7xl mx-auto py-8 space-y-6">
      <ShadowModeBanner context="WM Composer opera em shadow paralelo ao legacy. Concordância e p95 ditam promoção." />
      <header className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Radio className="h-7 w-7 text-primary" />
          WM Composer · Shadow Insights
        </h1>
        <p className="text-muted-foreground">
          Telemetria do orquestrador shadow vs decisão legacy. Janela: últimas {WINDOW_HOURS}h ·
          atualiza a cada {Math.round(REFRESH_MS / 1000)}s.
        </p>
      </header>

      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>Modo shadow</AlertTitle>
        <AlertDescription>
          O composer <strong>não influencia</strong> se a SYRA responde — apenas registra o que faria.
          A coluna <code className="font-mono text-xs">decision_source</code> distingue
          {" "}<code className="font-mono text-xs">shadow</code> (composer S04+S05) de
          {" "}<code className="font-mono text-xs">legacy</code> (router atual). A concordância abaixo
          é o critério final do <strong>Gate G1</strong>.
        </AlertDescription>
      </Alert>

      {errMsg && (
        <Alert variant="destructive">
          <AlertTitle>Erro ao carregar</AlertTitle>
          <AlertDescription>{errMsg}</AlertDescription>
        </Alert>
      )}

      {/* ── KPIs principais ── */}
      <section className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KpiCard
          icon={<Activity className="h-4 w-4" />}
          label="Decisões shadow"
          value={loading ? null : totalShadow.toLocaleString("pt-BR")}
          hint={`Legacy: ${totalLegacy.toLocaleString("pt-BR")} · janela ${WINDOW_HOURS}h`}
        />
        <KpiCard
          icon={<Clock className="h-4 w-4" />}
          label="Latência p95 (shadow)"
          value={loading ? null : `${p95} ms`}
          hint={`p50 ${p50}ms · p99 ${p99}ms`}
          tone={p95 > 600 ? "warn" : "ok"}
        />
        <KpiCard
          icon={<Target className="h-4 w-4" />}
          label="Decision · respond"
          value={
            loading
              ? null
              : `${byDecision.respond ?? 0} (${totalShadow ? Math.round((100 * (byDecision.respond ?? 0)) / totalShadow) : 0}%)`
          }
        />
        <KpiCard
          icon={<GitCompare className="h-4 w-4" />}
          label="Concordância shadow↔legacy"
          value={loading ? null : `${agreement.pct.toFixed(1)}%`}
          hint={`${agreement.agree.toLocaleString("pt-BR")} / ${agreement.paired.toLocaleString("pt-BR")} pareados`}
          tone={agreement.paired >= 100 && agreement.pct < 75 ? "warn" : "ok"}
        />
      </section>

      {/* ── S07: Comparação shadow vs legacy ── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GitCompare className="h-5 w-5" />
            Concordância shadow vs legacy
          </CardTitle>
          <CardDescription>
            Pareamento por <code className="font-mono text-xs">message_id</code>. Considera concordante
            quando ambos decidem responder ou ambos decidem ficar em silêncio.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : agreement.paired === 0 ? (
            <p className="text-sm text-muted-foreground">
              Sem turnos pareados na janela. Aguardando tráfego com ambas as fontes ativas.
            </p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <PairTile label="Ambos respond" n={agreement.bothRespond} total={agreement.paired} tone="ok" />
              <PairTile label="Ambos silent/defer" n={agreement.bothSilent} total={agreement.paired} tone="ok" />
              <PairTile
                label="Shadow respond · Legacy silent"
                n={agreement.shadowRespondLegacySilent}
                total={agreement.paired}
                tone="warn"
              />
              <PairTile
                label="Shadow silent · Legacy respond"
                n={agreement.shadowSilentLegacyRespond}
                total={agreement.paired}
                tone="warn"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── S08: Drift histórico + Canary control ── */}
      {/* CanaryControl removido em C4.3 */}

      {/* S10 DivergenceExplorer removido em Plano-13 (canary não existe mais). */}

      <Card>
        <CardHeader>
          <CardTitle>Distribuição por tipo de decisão (shadow)</CardTitle>
          <CardDescription>respond · silent · defer</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-24 w-full" />
          ) : (
            <div className="grid grid-cols-3 gap-4 text-center">
              {(["respond", "silent", "defer"] as const).map((d) => {
                const n = byDecision[d] ?? 0;
                const pct = totalShadow ? Math.round((100 * n) / totalShadow) : 0;
                return (
                  <div key={d} className="rounded-md border p-4 bg-card">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">{d}</p>
                    <p className="text-2xl font-semibold mt-1">{n.toLocaleString("pt-BR")}</p>
                    <p className="text-sm text-muted-foreground">{pct}%</p>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Top reason_codes ── */}
      <Card>
        <CardHeader>
          <CardTitle>Top reason codes (shadow)</CardTitle>
          <CardDescription>O motivo da decisão final do composer</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-32 w-full" />
          ) : byReason.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sem dados na janela.</p>
          ) : (
            <ul className="space-y-2">
              {byReason.map(([code, n]) => {
                const pct = totalShadow ? (100 * n) / totalShadow : 0;
                return (
                  <li key={code} className="flex items-center gap-3">
                    <Badge variant="outline" className="font-mono text-xs">
                      {code}
                    </Badge>
                    <div className="flex-1 h-2 bg-muted rounded overflow-hidden">
                      <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-sm tabular-nums w-20 text-right text-muted-foreground">
                      {n.toLocaleString("pt-BR")} · {pct.toFixed(1)}%
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* ── Sinais S05 ── */}
      <Card>
        <CardHeader>
          <CardTitle>Sinais alimentados (S05)</CardTitle>
          <CardDescription>
            Frequência dos sinais reais que chegam ao composer (% sobre o total shadow)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-24 w-full" />
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <SignalTile label="is_mention" n={sigStats.is_mention} total={totalShadow} />
              <SignalTile label="is_reply_to_agent" n={sigStats.is_reply_to_agent} total={totalShadow} />
              <SignalTile label="is_direct_dm" n={sigStats.is_direct_dm} total={totalShadow} />
              <SignalTile
                label="rule_3msgs ativo (≥3)"
                n={sigStats.rule_3msgs_active}
                total={totalShadow}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Gate G1 ── */}
      <Card>
        <CardHeader>
          <CardTitle>Gate G1 — critérios para canary 5%</CardTitle>
          <CardDescription>Quando promover a flag de shadow para rollout real</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <GateRow ok={totalShadow >= 5000} label={`Volume shadow ≥ 5.000 amostras (atual: ${totalShadow.toLocaleString("pt-BR")})`} />
          <GateRow ok={p95 > 0 && p95 < 600} label={`p95 latência composer < 600ms (atual: ${p95}ms)`} />
          <GateRow
            ok={agreement.paired >= 1000 && agreement.pct >= 75}
            label={`Concordância shadow↔legacy ≥ 75% sobre ≥1.000 pares (atual: ${agreement.pct.toFixed(1)}% sobre ${agreement.paired.toLocaleString("pt-BR")})`}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function KpiCard({
  icon,
  label,
  value,
  hint,
  tone = "ok",
}: {
  icon: React.ReactNode;
  label: string;
  value: string | null;
  hint?: string;
  tone?: "ok" | "warn";
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          {icon}
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {value === null ? (
          <Skeleton className="h-7 w-24" />
        ) : (
          <p
            className={`text-2xl font-semibold tabular-nums ${
              tone === "warn" ? "text-destructive" : ""
            }`}
          >
            {value}
          </p>
        )}
        {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
      </CardContent>
    </Card>
  );
}

function SignalTile({ label, n, total }: { label: string; n: number; total: number }) {
  const pct = total ? Math.round((100 * n) / total) : 0;
  return (
    <div className="rounded-md border p-3 bg-card">
      <p className="text-xs font-mono text-muted-foreground">{label}</p>
      <p className="text-xl font-semibold mt-1 tabular-nums">{n.toLocaleString("pt-BR")}</p>
      <p className="text-xs text-muted-foreground">{pct}% dos turnos</p>
    </div>
  );
}

function PairTile({
  label,
  n,
  total,
  tone,
}: {
  label: string;
  n: number;
  total: number;
  tone: "ok" | "warn";
}) {
  const pct = total ? (100 * n) / total : 0;
  return (
    <div className={`rounded-md border p-4 bg-card ${tone === "warn" && n > 0 ? "border-amber-500/40" : ""}`}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-2xl font-semibold mt-1 tabular-nums">{n.toLocaleString("pt-BR")}</p>
      <p className="text-sm text-muted-foreground">{pct.toFixed(1)}%</p>
    </div>
  );
}

function GateRow({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <span
        className={`inline-block h-2.5 w-2.5 rounded-full ${
          ok ? "bg-green-500" : "bg-amber-500"
        }`}
      />
      <span>{label}</span>
    </div>
  );
}
